import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Breadcrumbs } from '@mui/material';
import { Description as DescriptionIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import DateRangePicker from '../../../components/DateRangePicker';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import {
    fetchInit,
    fetchSystemLogs,
    selectSystemLogs,
    selectSystemLogsLoading,
    selectSystemLogsError,
    selectSystemLogsTotal,
    selectSystemLogsPage,
    selectSystemLogsPageSize,
    selectSystemLogsSortColumn,
    selectSystemLogsSortDirection,
    selectSystemLogsFilterTitle,
    selectSystemLogsFilterTypes,
    selectSystemLogsFilterActions,
    selectSystemLogsFilterCategories,
    selectSystemLogsFilterAccount,
    selectSystemLogsFilterFromDate,
    selectSystemLogsFilterToDate,
    selectSystemLogsTypes,
    selectSystemLogsActions,
    selectSystemLogsCategories,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterTitle,
    setFilterTypes,
    setFilterActions,
    setFilterCategories,
    setFilterAccount,
    setFilterFromDate,
    setFilterToDate,
    clearError,
} from './slice';
import './SystemLogs.css';

function SystemLogs() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const systemLogs = useSelector(selectSystemLogs);
    const loading = useSelector(selectSystemLogsLoading);
    const error = useSelector(selectSystemLogsError);
    const total = useSelector(selectSystemLogsTotal);
    const page = useSelector(selectSystemLogsPage);
    const pageSize = useSelector(selectSystemLogsPageSize);
    const sortColumn = useSelector(selectSystemLogsSortColumn);
    const sortDirection = useSelector(selectSystemLogsSortDirection);
    const filterTitle = useSelector(selectSystemLogsFilterTitle);
    const filterTypes = useSelector(selectSystemLogsFilterTypes);
    const filterActions = useSelector(selectSystemLogsFilterActions);
    const filterCategories = useSelector(selectSystemLogsFilterCategories);
    const filterAccount = useSelector(selectSystemLogsFilterAccount);
    const filterFromDate = useSelector(selectSystemLogsFilterFromDate);
    const filterToDate = useSelector(selectSystemLogsFilterToDate);
    const types = useSelector(selectSystemLogsTypes);
    const actions = useSelector(selectSystemLogsActions);
    const categories = useSelector(selectSystemLogsCategories);

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    // Fetch system logs when filters/pagination/sorting change
    useEffect(() => {
        // Build params matching backend API definition (camelCase)
        const params = {
            page,
            pageSize,
            sortColumn: sortColumn,
            sortDirection: sortDirection,
        };

        // Add content filter if provided
        if (filterTitle) {
            params.content = filterTitle;
        }

        // Add types as multiple params: types=0&types=1
        if (filterTypes.length > 0) {
            params.types = filterTypes;
        }

        // Add actions as multiple params: actions=0&actions=1
        if (filterActions.length > 0) {
            params.actions = filterActions;
        }

        // Add categories as multiple params: categories=Security&categories=Backup
        if (filterCategories.length > 0) {
            params.categories = filterCategories;
        }

        // Add account filter if provided
        if (filterAccount) {
            params.account = filterAccount;
        }

        // Add date range filter if provided
        if (filterFromDate) {
            params.fromDate = filterFromDate;
        }
        if (filterToDate) {
            params.toDate = filterToDate;
        }

        dispatch(fetchSystemLogs(params));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterTitle, filterTypes, filterActions, filterCategories, filterAccount, filterFromDate, filterToDate]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Handle filter changes
    const handleFilterTitleChange = (e) => {
        dispatch(setFilterTitle(e.target.value));
    };

    const handleFilterTypesChange = (e) => {
        // MultiSelect component returns array of IDs in e.target.value
        dispatch(setFilterTypes(e.target.value));
    };

    const handleFilterActionsChange = (e) => {
        // MultiSelect component returns array of IDs in e.target.value
        dispatch(setFilterActions(e.target.value));
    };

    const handleFilterCategoriesChange = (e) => {
        // MultiSelect component returns array of category keys in e.target.value
        dispatch(setFilterCategories(e.target.value));
    };

    const handleFilterAccountChange = (e) => {
        dispatch(setFilterAccount(e.target.value));
    };

    const handleFilterDateRangeFromChange = (e) => {
        dispatch(setFilterFromDate(e.target.value));
    };

    const handleFilterDateRangeToChange = (e) => {
        dispatch(setFilterToDate(e.target.value));
    };

    // Handle pagination
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    const handlePageSizeChange = (newSize) => {
        dispatch(setPageSize(newSize));
    };

    // Handle sorting
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Convert types, actions, and categories objects to arrays for MultiSelect
    const typesOptions = Object.keys(types).map((key) => ({
        id: key,
        name: types[key],
    }));

    const actionsOptions = Object.keys(actions).map((key) => ({
        id: key,
        name: actions[key],
    }));

    const categoriesOptions = Object.keys(categories).map((key) => ({
        id: key,
        name: categories[key],
    }));

    // Table columns
    const columns = [
        {
            key: 'createdOn',
            label: t('systemLogs.table.createdOn'),
            align: 'left',
            render: (value) => formatDateTimeWithCulture(value, i18n.language),
        },
        {
            key: 'type',
            label: t('systemLogs.table.type'),
            render: (value) => types[value] || value,
        },
        {
            key: 'action',
            label: t('systemLogs.table.action'),
            render: (value) => actions[value] || value,
        },
        { key: 'content', label: t('systemLogs.table.content') },
        {
            key: 'detail',
            label: t('systemLogs.table.detail'),
            render: (value) => (
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {value}
                </Typography>
            ),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DescriptionIcon fontSize="small" />
                        <Typography variant="body2">{t('systemLogs.breadcrumb.system')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('systemLogs.breadcrumb.systemLogs')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('systemLogs.title')}
                    </Typography>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        <MultiSelect
                            label={t('systemLogs.filterByTypes')}
                            value={filterTypes}
                            onChange={handleFilterTypesChange}
                            options={typesOptions}
                            placeholder={t('systemLogs.filterByTypesPlaceholder')}
                            id="filterTypes"
                            name="filterTypes"
                            data-testid="systemLogs-select-filterTypes"
                            sx={{ width: '300px' }}
                        />
                        <MultiSelect
                            label={t('systemLogs.filterByActions')}
                            value={filterActions}
                            onChange={handleFilterActionsChange}
                            options={actionsOptions}
                            placeholder={t('systemLogs.filterByActionsPlaceholder')}
                            id="filterActions"
                            name="filterActions"
                            data-testid="systemLogs-select-filterActions"
                            sx={{ width: '300px' }}
                        />
                        <MultiSelect
                            label={t('systemLogs.filterByCategories')}
                            value={filterCategories}
                            onChange={handleFilterCategoriesChange}
                            options={categoriesOptions}
                            placeholder={t('systemLogs.filterByCategoriesPlaceholder')}
                            id="filterCategories"
                            name="filterCategories"
                            data-testid="systemLogs-select-filterCategories"
                            sx={{ width: '300px' }}
                        />
                        <Input
                            label={t('systemLogs.filterByAccount') || 'Lọc theo tài khoản'}
                            value={filterAccount}
                            onChange={handleFilterAccountChange}
                            placeholder={t('systemLogs.filterByAccountPlaceholder') || 'Nhập tên đăng nhập...'}
                            id="filterAccount"
                            name="filterAccount"
                            data-testid="systemLogs-input-filterAccount"
                            sx={{ width: '300px' }}
                        />
                        <DateRangePicker
                            label={t('systemLogs.filterByDateRange')}
                            fromValue={filterFromDate}
                            toValue={filterToDate}
                            onFromChange={handleFilterDateRangeFromChange}
                            onToChange={handleFilterDateRangeToChange}
                            id="filterDateRange"
                            name="filterDateRange"
                            data-testid="systemLogs-daterange-filter"
                            sx={{ width: '300px' }}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="systemLogs-table"
                        columns={columns}
                        data={systemLogs}
                        selectable={false}
                        loading={loading}
                        emptyMessage={t('systemLogs.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                    />
                </Box>
            </Paper>
        </Box>
    );
}

export default SystemLogs;
